import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import clsx from "clsx"
import GameScreenLayout from "@/components/GameScreenLayout"
import { ColorRank, DeckSearchMode } from "@/types/battle"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"
import {
  buildDeckRadarStatusRequest,
  formatDeckSearchModeLabel,
} from "@/utils/deckSearch"
import { RANK_NAMES } from "@/constants/guardians"
import {
  connectBattleSocket,
  emitBattleEvent,
  emitBattleEventWhenReady,
  onBattleEvent,
} from "@/socket/battleSocket"
import { normalizeDeckRadarStatus } from "@/utils/deckPayload"
import { useBattleDeckChallenge } from "@/features/battle/DeckChallengeContext"
import { useAuth } from "@/context/AuthContext"
import { GuardianCard } from "@/components/GuardianCard"

const RANKS: ColorRank[] = ["WHITE", "GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

export default function DeckBuilderPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const acceptMode = searchParams.get("mode") === "accept"
  const acceptChallengeId = searchParams.get("challengeId") ?? ""
  const acceptDeckMode = (searchParams.get("deckSearchMode") as DeckSearchMode) || "ANY"
  const acceptRank = (searchParams.get("colorRank") as ColorRank) || "WHITE"

  const { token } = useAuth()
  const { markDeckAcceptPending } = useBattleDeckChallenge()
  const { collectionCards } = useGuardiansPassport()
  const [mode, setMode] = useState<DeckSearchMode>(
    acceptMode ? acceptDeckMode : "RANK"
  )
  const [rank, setRank] = useState<ColorRank>(
    acceptMode ? acceptRank : "WHITE"
  )
  const [picked, setPicked] = useState<string[]>([])
  const [statusText, setStatusText] = useState("")

  const deckSize = 6

  const eligible = useMemo(() => {
    if (mode === "ANY") return collectionCards
    return collectionCards.filter((c) => c.colorRank === rank)
  }, [collectionCards, mode, rank])

  useEffect(() => {
    void connectBattleSocket().catch(() => {})
    const offStarted = onBattleEvent("deck-battle-started", (payload) => {
      navigate("/play/deck-duel", { state: { battle: payload } })
    })
    const offStatus = onBattleEvent("deck-radar-status", (raw) => {
      const s = normalizeDeckRadarStatus(raw)
      if (s) {
        setStatusText(
          `Очередь ${s.queueSize}, матчей ${s.matchableCount}, энергия ${s.energyCost}`
        )
      }
    })
    return () => {
      offStarted()
      offStatus()
    }
  }, [navigate])

  function toggleCard(id: string) {
    setPicked((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= deckSize) return prev
      return [...prev, id]
    })
  }

  function refreshStatus() {
    emitBattleEvent(
      "deck-radar-status",
      buildDeckRadarStatusRequest(mode, rank)
    )
  }

  function search() {
    if (picked.length !== deckSize) {
      setStatusText(`Нужно ${deckSize} карт`)
      return
    }
    const sp = new URLSearchParams()
    sp.set("deckSearchMode", mode)
    if (mode === "RANK") sp.set("colorRank", rank)
    picked.forEach((id) => sp.append("cardIds", id))
    sp.set("autoStart", "1")
    navigate(`/play/deck-matchmaking?${sp.toString()}`)
  }

  function cancel() {
    emitBattleEvent("deck-radar-cancel")
    refreshStatus()
  }

  async function acceptChallenge() {
    if (!acceptChallengeId || picked.length !== deckSize) {
      setStatusText(`Нужно ${deckSize} карт для принятия`)
      return
    }
    markDeckAcceptPending()
    const sent = await emitBattleEventWhenReady(token, "deck-challenge-accept", {
      challengeId: acceptChallengeId,
      cardIds: picked,
    })
    if (!sent) {
      setStatusText("Сокет не готов")
    } else {
      setStatusText("Принято — ждём старт боя…")
    }
  }

  return (
    <GameScreenLayout title={acceptMode ? "Принять вызов" : "Колодный бой"} backTo="/play">
      <p className="text-xs text-guardians-muted">
        {formatDeckSearchModeLabel(mode, rank)} · выбрано {picked.length}/{deckSize}
      </p>

      {acceptMode ? (
        <p className="mb-3 text-sm text-guardians-accent">
          Соберите колоду для входящего вызова
        </p>
      ) : null}

      <div className="my-3 flex gap-2">
        <button
          type="button"
          disabled={acceptMode}
          onClick={() => setMode("RANK")}
          className={clsx(
            "flex-1 rounded-lg py-2 text-xs",
            mode === "RANK" ? "bg-guardians-accent text-guardians-bg" : "border border-white/20"
          )}
        >
          По рангу
        </button>
        <button
          type="button"
          disabled={acceptMode}
          onClick={() => setMode("ANY")}
          className={clsx(
            "flex-1 rounded-lg py-2 text-xs",
            mode === "ANY" ? "bg-guardians-accent text-guardians-bg" : "border border-white/20"
          )}
        >
          Любые 6
        </button>
      </div>

      {mode === "RANK" ? (
        <select
          value={rank}
          onChange={(e) => {
            setRank(e.target.value as ColorRank)
            setPicked([])
          }}
          className="mb-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm"
        >
          {RANKS.map((r) => (
            <option key={r} value={r}>
              {RANK_NAMES[r]}
            </option>
          ))}
        </select>
      ) : null}

      <ul className="mb-4 grid max-h-80 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
        {eligible.map((card) => {
          const selected = picked.includes(card.id)
          return (
            <li key={card.id} className="flex justify-center">
              <GuardianCard
                card={card}
                size="sm"
                selected={selected}
                onClick={() => toggleCard(card.id)}
              />
            </li>
          )
        })}
      </ul>

      <p className="mb-3 text-sm text-guardians-muted">{statusText || "Статус радара"}</p>

      <div className="flex flex-wrap gap-2">
        {acceptMode ? (
          <button
            type="button"
            onClick={() => void acceptChallenge()}
            className="rounded-xl bg-guardians-accent px-4 py-2 text-sm text-guardians-bg"
          >
            Принять вызов
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={search}
              className="rounded-xl bg-guardians-accent px-4 py-2 text-sm text-guardians-bg"
            >
              Искать колодный бой
            </button>
            <button type="button" onClick={cancel} className="rounded-xl border border-white/20 px-4 py-2 text-sm">
              Отмена
            </button>
            <button type="button" onClick={refreshStatus} className="rounded-xl border border-white/20 px-4 py-2 text-sm">
              Статус
            </button>
          </>
        )}
      </div>
    </GameScreenLayout>
  )
}
